import React, { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import ArrowDropUpOutlinedIcon from "@mui/icons-material/ArrowDropUpOutlined";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import "./Table.css";

const EMPTY_COLUMNS = [];
const EMPTY_DATA = [];

const DataTable = ({
  columns,
  data,
  currentPage,
  setCurrentPage,
  selectedRows,
  pageSize = 20,
  loading = false,
  tableFooter =  true,
}) => {
  const tableColumns = useMemo(
    () => (Array.isArray(columns) ? columns : EMPTY_COLUMNS),
    [columns],
  );
  const tableData = useMemo(
    () => (Array.isArray(data) ? data : EMPTY_DATA),
    [data],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize: currentPageSize },
  } = useTable(
    {
      columns: tableColumns,
      data: tableData,
      initialState: { pageIndex: currentPage, pageSize },
      autoResetPage: false,
      autoResetSortBy: false,
    },
    useSortBy,
    usePagination,
  );

  const generateSortingIndicator = useCallback((column) => {
    if (column.disableSortBy) return null;

    const isSorted = column.isSorted;
    const isSortedDesc = column.isSortedDesc;

    return (
      <div className="ml-1 flex flex-col items-center justify-center">
        <ArrowDropUpOutlinedIcon
          className={`${
            isSorted && !isSortedDesc ? "text-primary" : "text-primary/20"
          }`}
          fontSize="small"
          style={{ marginBottom: "-6px" }}
        />
        <ArrowDropDownOutlinedIcon
          className={`${
            isSorted && isSortedDesc ? "text-primary" : "text-primary/20"
          }`}
          fontSize="small"
          style={{ marginTop: "-6px" }}
        />
      </div>
    );
  }, []);

  const scrollContainerRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const [isPageSizeDropdownOpen, setIsPageSizeDropdownOpen] = useState(false);
  const pageSizeDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pageSizeDropdownRef.current &&
        !pageSizeDropdownRef.current.contains(event.target)
      ) {
        setIsPageSizeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pageSizeOptions = [10, 20, 30, 50, 100];

  const previousCurrentPageRef = useRef(currentPage);

  // Pull only a GENUINE external currentPage change (e.g. a parent reset on
  // search/filter) down into the table. Echoes of our own setCurrentPage are
  // ignored — otherwise the pull-down would snap pageIndex back on every page
  // click and ping-pong with the push-up effect, making the rows flicker.
  useEffect(() => {
    const externalChange = previousCurrentPageRef.current !== currentPage;
    previousCurrentPageRef.current = currentPage;
    const target = Number(currentPage || 0);
    if (externalChange && Number.isFinite(target) && target !== pageIndex) {
      gotoPage(target);
    }
  }, [currentPage, gotoPage, pageIndex]);

  // Push internal page changes (prev/next/page-size) up to the parent.
  useEffect(() => {
    if (
      typeof setCurrentPage === "function" &&
      pageIndex !== Number(currentPage || 0)
    ) {
      setCurrentPage(pageIndex);
    }
  }, [currentPage, pageIndex, setCurrentPage]);

  return (
    <div className="flex flex-col w-full" ref={tableWrapperRef}>
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className="w-full table-scroll-container"
          style={{ overflowX: "auto", overflowY: "visible" }}
        >
          <table
            {...getTableProps()}
            className="border-collapse"
            style={{ width: "100%", minWidth: "600px" }}
          >
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr
                    key={headerGroupKey}
                    {...headerGroupProps}
                  >
                    {headerGroup.headers.map((column) => {
                      const { key: columnKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <th
                          key={columnKey}
                          {...columnProps}
                          className="px-2 py-2 bg-light text-primary font-bold text-left whitespace-nowrap text-xs sm:text-sm tracking-wider"
                        >
                          <div className="flex items-center gap-1">
                            {column.render("Header")}
                            {generateSortingIndicator(column)}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {loading
                ? Array.from({ length: pageSize > 10 ? 8 : pageSize }).map((_, rowIdx) => (
                    <tr key={rowIdx} className="border-t border-line bg-white">
                      {tableColumns.map((col, colIdx) => (
                        <td key={colIdx} className="px-2 py-2">
                          <Skeleton
                            variant="rectangular"
                            height={18}
                            width={colIdx === 0 ? 24 : "70%"}
                            sx={{ borderRadius: 1 }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                : page.map((row) => {
                prepareRow(row);
                const isSelected = selectedRows?.has(row.original.client_id);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr
                    key={rowKey}
                    {...rowProps}
                    className={`border-t border-line hover:bg-line/30 bg-white ${
                      isSelected ? "bg-blue-50" : ""
                    }`}
                  >
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps();
                      return (
                        <td
                          key={cellKey}
                          {...cellProps}
                          className="p-2 text-content text-left text-xs align-middle"
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
{
 tableFooter && 
      <div className="px-3 py-1.5 pt-3 flex justify-between items-center bg-white">
        <div>
          {loading && (
            <span className="text-xs text-muted flex items-center gap-1.5">
              <CircularProgress size={14} thickness={5} sx={{ color: "var(--color-primary, #002841)" }} />
              Loading...
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-content font-medium">Page</span>
          <button
            onClick={previousPage}
            disabled={!canPreviousPage || loading}
            className="w-5 h-5 flex items-center justify-center text-primary hover:bg-light rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous Page"
          >
            <KeyboardArrowLeftIcon fontSize="small" />
          </button>
          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary text-white text-xs font-bold border border-primary mx-1">
            {pageIndex + 1}
          </span>
          <button
            onClick={nextPage}
            disabled={!canNextPage || loading}
            className="w-5 h-5 flex items-center justify-center text-primary hover:bg-light rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next Page"
          >
            <KeyboardArrowRightIcon fontSize="small" />
          </button>
          <div className="relative ml-4" ref={pageSizeDropdownRef}>
            <button
              onClick={() => setIsPageSizeDropdownOpen(!isPageSizeDropdownOpen)}
              className="px-2 py-1 border-b border-mutedDark text-sm bg-white text-primary focus:outline-none min-w-[60px] flex items-center justify-between gap-2"
            >
              <span>{currentPageSize}</span>
              <ExpandLessIcon
                fontSize="small"
                className={`transition-transform ${
                  isPageSizeDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isPageSizeDropdownOpen && (
              <div 
                className={`absolute right-0 bg-white border border-line rounded-lg shadow-lg z-[9999] min-w-[60px] py-1 bottom-full mb-1 
                  ${ tableData.length <= 3 && "h-20 overflow-y-auto" }`}
              >
                {pageSizeOptions.map((size) => (
                  <button
                    key={size}
                    onClick={() => {
                      const newSize = Number(size);
                      gotoPage(0);
                      if (typeof setPageSize === "function")
                        setPageSize(newSize);
                      setIsPageSizeDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                      currentPageSize === size
                        ? "bg-primary/10 text-primary font-bold"
                        : "text-content"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
}
    </div>
  );
};

export default DataTable;
